import { useSyncExternalStore } from 'react';
import { apiConfig } from '../api/config';
import { readCurrentAuthorization, readCurrentUserId } from '../api/core/authorization';
import { createServiceClient } from '../api/http/client';
import { dataErrorEnvelope } from '../api/http/envelopes';
import {
  clearOperationalCache,
  clearOperationalDomainCache,
  queryClient,
} from '../api/query-client';
import { readOperatorConfig } from './config';
import { createOperatorSession, type OperatorSession } from './session';

export const operatorConfig = readOperatorConfig(
  {
    authorizeUrl: import.meta.env.VITE_OPERATOR_AUTHORIZE_URL,
    clientId: import.meta.env.VITE_OPERATOR_CLIENT_ID,
    audience: import.meta.env.VITE_OPERATOR_AUDIENCE,
    redirectUri: import.meta.env.VITE_OPERATOR_REDIRECT_URI,
    appOrigin: window.location.origin,
    relayOrigins: import.meta.env.VITE_OPERATOR_RELAY_ORIGINS,
    coreApiUrl: apiConfig.coreApiUrl,
  },
  { isDevelopment: import.meta.env.DEV },
);

// 세션이 아직 토큰을 들고 있지 않은 로그인 직후 호출용 — 받은 토큰을 직접 붙인다.
const coreClient = (token: string) =>
  createServiceClient('core', {
    label: '코어 API',
    baseUrl: operatorConfig?.coreApiUrl,
    envKey: 'VITE_CORE_API_URL',
    envelope: dataErrorEnvelope,
    arrayFormat: 'repeat',
    auth: { scheme: 'bearer', required: true, tokens: { getToken: () => token } },
    allowDevelopmentCoreProxyHttp: import.meta.env.DEV,
  });

export const operatorSession = createOperatorSession({
  config: operatorConfig,
  fetch: (input, init) => globalThis.fetch(input, init),
  // 저장소 접근을 로그인 시점까지 늦춘다. storage 차단도 가짜 로그인 성공으로 만들지 않는다.
  storage: {
    getItem: (key) => sessionStorage.getItem(key),
    setItem: (key, value) => {
      sessionStorage.setItem(key, value);
    },
    removeItem: (key) => {
      sessionStorage.removeItem(key);
    },
  },
  // refresh 토큰만 localStorage에 둔다 — 새로고침·새 탭·새 창이 같은 로그인을 이어 받는다. access 토큰은 메모리에만 둔다.
  // 접근은 호출 시점까지 늦추고, 차단돼도 메모리 세션으로 동작한다(세션 session.ts의 try/catch).
  persistentStorage: {
    getItem: (key) => localStorage.getItem(key),
    setItem: (key, value) => {
      localStorage.setItem(key, value);
    },
    removeItem: (key) => {
      localStorage.removeItem(key);
    },
  },
  // 두 탭이 같은 refresh 토큰을 동시에 쓰면 토큰 회전·재사용 감지로 로그인이 풀릴 수 있어 탭 사이 직렬화한다.
  withRefreshLock: (task) =>
    'locks' in navigator ? navigator.locks.request('core-admin.oauth.refresh', task) : task(),
  subscribeStorage: (listener) => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === localStorage) listener(event.key, event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  },
  clearOperationalCache,
  clearDomainOperationalCache: (domain, generation) => {
    clearOperationalDomainCache(
      domain,
      generation,
      operatorConfig?.coreApiUrl ?? 'unconfigured',
      queryClient,
    );
  },
  resolveMember: async (token, signal) => {
    await coreClient(token).post<unknown>('users/me/resolve', { signal });
  },
  loadAuthorization: (token, signal) => readCurrentAuthorization(coreClient(token), signal),
  loadIdentity: (token, signal) => readCurrentUserId(coreClient(token), signal),
});

export function useOperatorSession(session: OperatorSession = operatorSession) {
  return useSyncExternalStore(session.subscribe, session.getSnapshot, session.getSnapshot);
}

export interface CallbackResult {
  returnTo: string;
  failed: boolean;
}
let callbackResult: Promise<CallbackResult> | undefined;
/** main이 React/Router 렌더 전에 한 번 호출한다. Promise가 StrictMode mount와 무관하게 교환을 소유한다. */
export function initializeOperatorCallback(): void {
  const url = new URL(window.location.href);
  if (
    callbackResult ||
    !(
      url.searchParams.has('code') ||
      url.searchParams.has('state') ||
      url.searchParams.has('error')
    )
  )
    return;
  callbackResult = operatorSession
    .completeCallback(url.href, () => {
      window.history.replaceState(window.history.state, '', url.pathname);
    })
    .then(
      (returnTo) => ({ returnTo, failed: false }),
      () => ({ returnTo: '/properties', failed: true }),
    );
}
export const getOperatorCallbackResult = () => callbackResult;
